// Reusable export button — enhanced Feb-2026.
//
// Features:
//   • Custom column picker (tick which columns to include; order follows tick order)
//   • Multiple formats: CSV / Excel (.xlsx) / PDF
//   • Optional date range / month / all-rows filter (unchanged from earlier API)
//   • Optional multi-value filter (e.g. company scope)
//
// Backward compatibility:
//   Existing call sites (e.g. `<TableExportButton columns={…} rows={…} />`) work
//   unchanged — the column picker opens with every column pre-ticked so the
//   default UX is "export everything" just like before, but the user can now
//   customise.

import React, { useState, useRef, useEffect, useMemo } from "react";
import { Download, ChevronDown, FileSpreadsheet, FileText, FileCode, CheckSquare, Square } from "lucide-react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// ------------------------ helpers ------------------------
const _cellValue = (row, col) =>
  typeof col.format === "function" ? col.format(row) : row[col.key];

const _toCsvCell = (v) => {
  if (v == null) return "";
  const s = String(v);
  if (/[",\n\r]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
  return s;
};

const _downloadCsv = (filename, cols, rows) => {
  const header = cols.map((c) => _toCsvCell(c.label || c.key)).join(",");
  const body = rows
    .map((r) => cols.map((c) => _toCsvCell(_cellValue(r, c))).join(","))
    .join("\n");
  const csv = "\uFEFF" + header + "\n" + body;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename.endsWith(".csv") ? filename : `${filename}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const _downloadXlsx = (filename, cols, rows) => {
  const header = cols.map((c) => c.label || c.key);
  const body = rows.map((r) => cols.map((c) => _cellValue(r, c) ?? ""));
  const ws = XLSX.utils.aoa_to_sheet([header, ...body]);
  ws["!cols"] = header.map((h, i) => {
    const maxLen = Math.max(
      String(h).length,
      ...body.slice(0, 100).map((row) => String(row[i] ?? "").length),
    );
    return { wch: Math.min(Math.max(maxLen + 2, 10), 40) };
  });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
  const name = filename.endsWith(".xlsx") ? filename : `${filename}.xlsx`;
  XLSX.writeFile(wb, name);
};

const _downloadPdf = (filename, cols, rows, title) => {
  const orientation = cols.length > 6 ? "landscape" : "portrait";
  const doc = new jsPDF({ orientation, unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  if (title) {
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text(title, pageWidth / 2, 32, { align: "center" });
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text(`Generated ${new Date().toLocaleString()}`, pageWidth / 2, 46, { align: "center" });
  }
  const head = [cols.map((c) => c.label || c.key)];
  const body = rows.map((r) =>
    cols.map((c) => {
      const v = _cellValue(r, c);
      return v == null ? "" : String(v);
    })
  );
  autoTable(doc, {
    head,
    body,
    startY: title ? 60 : 40,
    styles: { fontSize: 7, cellPadding: 3, overflow: "linebreak" },
    headStyles: { fillColor: [29, 41, 81], textColor: 255, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    margin: { top: 40, right: 20, bottom: 30, left: 20 },
  });
  const name = filename.endsWith(".pdf") ? filename : `${filename}.pdf`;
  doc.save(name);
};

// ------------------------ component ------------------------
export default function TableExportButton({
  filename = "export",
  rows = [],
  columns = [],
  dateField = "created_at",
  defaultMonth = new Date().getMonth(),
  defaultYear = new Date().getFullYear(),
  testIdPrefix = "export",
  filterField = "",
  filterOptions = [],
  filterLabel = "Filter",
  title = "",   // used as the PDF header title (defaults to filename)
}) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("all");        // "all" | "range" | "month"
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [month, setMonth] = useState(defaultMonth);
  const [year, setYear] = useState(defaultYear);
  const [pickedFilterValues, setPickedFilterValues] = useState([]);
  // Column picker — array of ticked keys, in the order the user ticked them.
  // Default: everything ticked in the original column order.
  const [pickedCols, setPickedCols] = useState(() => columns.map((c) => c.key));
  const [format, setFormat] = useState("xlsx");   // "xlsx" | "csv" | "pdf"
  const wrapRef = useRef(null);

  // Keep the picked-cols default in sync if the caller ever passes new columns.
  useEffect(() => {
    setPickedCols(columns.map((c) => c.key));
  }, [columns.length]);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const _filter = () => {
    let out = rows;
    if (filterField && pickedFilterValues.length > 0) {
      const picked = new Set(pickedFilterValues);
      out = out.filter((r) => picked.has(String(r[filterField] ?? "")));
    }
    if (mode === "all") return out;
    if (mode === "range") {
      if (!from && !to) return out;
      return out.filter((r) => {
        const d = String(r[dateField] || "").slice(0, 10);
        if (!d) return false;
        if (from && d < from) return false;
        if (to && d > to) return false;
        return true;
      });
    }
    if (mode === "month") {
      const m = String(month + 1).padStart(2, "0");
      const prefix = `${year}-${m}`;
      return out.filter((r) => String(r[dateField] || "").startsWith(prefix));
    }
    return out;
  };

  const toggleFilterVal = (v) =>
    setPickedFilterValues((s) => (s.includes(v) ? s.filter((x) => x !== v) : [...s, v]));
  const toggleAllFilterVals = () =>
    setPickedFilterValues((s) => (s.length === filterOptions.length ? [] : [...filterOptions]));

  const toggleCol = (key) =>
    setPickedCols((s) => (s.includes(key) ? s.filter((k) => k !== key) : [...s, key]));
  const toggleAllCols = () =>
    setPickedCols((s) => (s.length === columns.length ? [] : columns.map((c) => c.key)));

  const resolvedCols = useMemo(() => {
    // Preserve the user's tick order — that becomes the column order in the file.
    const map = new Map(columns.map((c) => [c.key, c]));
    return pickedCols.map((k) => map.get(k)).filter(Boolean);
  }, [pickedCols, columns]);

  const handleDownload = () => {
    if (resolvedCols.length === 0) return;
    const data = _filter();
    let stamp = "";
    if (mode === "range") stamp = `_${from || "open"}_to_${to || "open"}`;
    else if (mode === "month") stamp = `_${year}-${String(month + 1).padStart(2, "0")}`;
    const base = `${filename}${stamp}`;
    if (format === "csv") _downloadCsv(base, resolvedCols, data);
    else if (format === "xlsx") _downloadXlsx(base, resolvedCols, data);
    else if (format === "pdf") _downloadPdf(base, resolvedCols, data, title || filename.replace(/_/g, " "));
    setOpen(false);
  };

  const yearOpts = [year + 1, year, year - 1, year - 2, year - 3].filter((y, i, arr) => arr.indexOf(y) === i);

  const allColsPicked = pickedCols.length === columns.length;

  return (
    <div className="relative inline-block" ref={wrapRef}>
      <button
        onClick={() => setOpen(!open)}
        data-testid={`${testIdPrefix}-toggle`}
        className="border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-semibold px-3 py-1.5 rounded-md inline-flex items-center gap-1.5"
      >
        <Download size={13} /> Export <ChevronDown size={12} />
      </button>
      {open && (
        <div
          className="absolute right-0 mt-1 z-40 bg-white border border-slate-200 rounded-md shadow-lg p-3 w-96 max-h-[85vh] overflow-y-auto"
          data-testid={`${testIdPrefix}-panel`}
        >
          {/* ------ Format ------ */}
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1.5">Format</div>
          <div className="grid grid-cols-3 gap-1 mb-3">
            {[
              { k: "xlsx", lbl: "Excel", icon: FileSpreadsheet },
              { k: "pdf", lbl: "PDF", icon: FileText },
              { k: "csv", lbl: "CSV", icon: FileCode },
            ].map((opt) => {
              const Icon = opt.icon;
              return (
                <button key={opt.k}
                  onClick={() => setFormat(opt.k)}
                  data-testid={`${testIdPrefix}-fmt-${opt.k}`}
                  className={`px-2 py-1.5 text-xs font-semibold rounded inline-flex items-center justify-center gap-1 ${
                    format === opt.k ? "bg-[#1D2951] text-white" : "bg-slate-50 text-slate-700 hover:bg-slate-100"
                  }`}>
                  <Icon size={12} /> {opt.lbl}
                </button>
              );
            })}
          </div>

          {/* ------ Column picker ------ */}
          <div className="mb-3 border-t border-slate-200 pt-3">
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
                Columns · <span className="font-mono normal-case text-slate-700">{pickedCols.length}/{columns.length}</span>
              </div>
              <button
                onClick={toggleAllCols}
                data-testid={`${testIdPrefix}-cols-toggle-all`}
                className="text-[10px] text-[#1D2951] font-semibold hover:underline inline-flex items-center gap-1"
              >
                {allColsPicked ? <><Square size={11} /> Deselect all</> : <><CheckSquare size={11} /> Select all</>}
              </button>
            </div>
            <div className="max-h-48 overflow-y-auto border border-slate-200 rounded p-2 space-y-1 bg-slate-50">
              {columns.map((c) => {
                const idx = pickedCols.indexOf(c.key);
                const active = idx !== -1;
                return (
                  <label key={c.key} className="flex items-center gap-2 text-xs cursor-pointer hover:text-[#1D2951]">
                    <input
                      type="checkbox"
                      checked={active}
                      onChange={() => toggleCol(c.key)}
                      data-testid={`${testIdPrefix}-col-${c.key}`}
                      className="rounded"
                    />
                    <span className="flex-1 truncate">{c.label || c.key}</span>
                    {active && (
                      <span className="text-[9px] font-mono text-slate-400 bg-white border border-slate-200 rounded px-1">
                        #{idx + 1}
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
            <div className="text-[10px] text-slate-500 mt-1 italic">
              Columns appear in the file in the order you ticked them.
            </div>
          </div>

          {/* ------ Row scope ------ */}
          <div className="mb-2 border-t border-slate-200 pt-3">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1.5">Rows to Include</div>
            <div className="grid grid-cols-3 gap-1 mb-3">
              {[
                { k: "all", lbl: "All Rows" },
                { k: "range", lbl: "Date Range" },
                { k: "month", lbl: "Month" },
              ].map((opt) => (
                <button key={opt.k}
                  onClick={() => setMode(opt.k)}
                  data-testid={`${testIdPrefix}-mode-${opt.k}`}
                  className={`px-2 py-1 text-xs font-semibold rounded ${
                    mode === opt.k ? "bg-[#1D2951] text-white" : "bg-slate-50 text-slate-700 hover:bg-slate-100"
                  }`}>
                  {opt.lbl}
                </button>
              ))}
            </div>

            {mode === "range" && (
              <div className="space-y-2 mb-3">
                <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold block">From</label>
                <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
                  data-testid={`${testIdPrefix}-from`}
                  className="w-full border border-slate-300 rounded px-2 py-1 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-[#1D2951]/40" />
                <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold block">To</label>
                <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
                  data-testid={`${testIdPrefix}-to`}
                  className="w-full border border-slate-300 rounded px-2 py-1 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-[#1D2951]/40" />
              </div>
            )}

            {mode === "month" && (
              <div className="space-y-2 mb-3">
                <div className="flex gap-2">
                  <select value={year} onChange={(e) => setYear(Number(e.target.value))}
                    data-testid={`${testIdPrefix}-year`}
                    className="flex-1 border border-slate-300 rounded px-2 py-1 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-[#1D2951]/40">
                    {yearOpts.map((y) => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-4 gap-1">
                  {MONTH_NAMES.map((m, idx) => {
                    const active = idx === month;
                    return (
                      <button key={m}
                        onClick={() => setMonth(idx)}
                        data-testid={`${testIdPrefix}-month-${m.toLowerCase()}`}
                        className={`px-1 py-1 text-[11px] font-semibold rounded ${
                          active ? "bg-[#1D2951] text-white" : "bg-slate-50 text-slate-700 hover:bg-slate-100"
                        }`}>
                        {m}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* ------ Optional per-value filter (e.g. Company) ------ */}
          {filterField && filterOptions.length > 0 && (
            <div className="mb-3 border-t border-slate-200 pt-3">
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{filterLabel}</div>
                <button
                  onClick={toggleAllFilterVals}
                  data-testid={`${testIdPrefix}-filter-toggle-all`}
                  className="text-[10px] text-[#1D2951] font-semibold hover:underline"
                >
                  {pickedFilterValues.length === filterOptions.length ? "Clear all" : "Select all"}
                </button>
              </div>
              <div className="max-h-32 overflow-y-auto border border-slate-200 rounded p-2 space-y-1 bg-slate-50">
                {filterOptions.map((opt) => (
                  <label key={opt} className="flex items-center gap-2 text-xs cursor-pointer hover:text-[#1D2951]">
                    <input
                      type="checkbox"
                      checked={pickedFilterValues.includes(opt)}
                      onChange={() => toggleFilterVal(opt)}
                      data-testid={`${testIdPrefix}-filter-${opt}`}
                      className="rounded"
                    />
                    <span className="truncate">{opt || "(blank)"}</span>
                  </label>
                ))}
              </div>
              {pickedFilterValues.length === 0 && (
                <div className="text-[10px] text-slate-400 mt-1 italic">
                  Nothing ticked → all values are exported. Tick to filter.
                </div>
              )}
            </div>
          )}

          {/* ------ Summary ------ */}
          <div className="text-[10px] text-slate-500 mb-2">
            {(() => {
              const n = _filter().length;
              const cCount = resolvedCols.length;
              return `Will export ${n} of ${rows.length} row${rows.length === 1 ? "" : "s"} · ${cCount} column${cCount === 1 ? "" : "s"}`;
            })()}
          </div>
          <button
            onClick={handleDownload}
            disabled={resolvedCols.length === 0}
            data-testid={`${testIdPrefix}-download`}
            className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-semibold px-3 py-1.5 rounded-md inline-flex items-center justify-center gap-1.5">
            <Download size={13} /> Download {format === "xlsx" ? "Excel" : format.toUpperCase()}
          </button>
        </div>
      )}
    </div>
  );
}
